import { planCanadaPurchaseTax, allocateBreakdownTax } from './canadaTax.js';
import { computeProRataSplit, itemsForTarget } from './costSplit.js';
import { hasLineTaxOverrides, planLineTax } from './lineTaxRates.js';

/**
 * JobTread vendor-bill body for a reviewed direct bill.
 * Same figures the review screen sends, so Approve & Sync matches that sync.
 */
export function buildDirectBillJtPayload({
  extractedData,
  lineItemAssignments,
  costItems,
  isJobMode,
  matchedJob,
  matchedPO,
  selectedVendorId,
  markAsPaid,
  upload,
  xeroBillId,
  isCreditNote,
  xeroCurrency = null,
  xeroCurrencyRate = null,
  caProvince,
  country,
  splitMode = false,
  splitTarget = null,
  splitCostTypes = [],
  costGroups = null
}) {
  const jobId = matchedJob?.jobId || matchedPO?.jobId;
  const allCostItems = costItems || [];
  const hasTax = extractedData.hasTax || false;
  const lineAmountTypes = hasTax ? 'Exclusive' : 'NoTax';

  const eligibleLines = (extractedData.lineItems || []).filter((lineItem) => {
    return (lineItem.quantity ?? 1) !== 0 || (lineItem.amount || 0) !== 0;
  });

  const eligibleSubtotal = eligibleLines.reduce((sum, li) => sum + (li.amount || 0), 0);
  const jtCaPlan = caProvince && hasTax
    ? planCanadaPurchaseTax({ province: caProvince, subtotal: eligibleSubtotal, breakdown: extractedData.taxBreakdown || extractedData.taxLines })
    : null;
  const invoiceTaxTotal = jtCaPlan ? jtCaPlan.postedTax : (extractedData.taxAmount || 0);
  // Per-line rates: each line's own tax (and PST share in a province with PST).
  // The other lines stay at the bill rate saved when the first override was set.
  const provincePlan = caProvince ? planCanadaPurchaseTax({ province: caProvince, subtotal: 0 }) : null;
  const linePlan = hasLineTaxOverrides(eligibleLines)
    ? planLineTax(eligibleLines, {
        defaultRatePct: Number(extractedData.lineTaxDefaultRate) || 0,
        recoverableRatePct: provincePlan?.hasPst ? provincePlan.recoverableRatePct : null
      })
    : null;
  const reviewedTax = !linePlan && jtCaPlan && extractedData.taxBreakdownAuto === false
    ? allocateBreakdownTax(eligibleLines.map(li => li.amount || 0), jtCaPlan.components)
    : null;

  const jtItems = eligibleLines.map((lineItem, lineIndex) => {
    const origIndex = extractedData.lineItems.indexOf(lineItem);
    const costItemId = lineItemAssignments[origIndex];
    const costItem = allCostItems.find(ci => ci.id === costItemId);
    const quantity = lineItem.quantity ?? 1;
    const unitAmount = (quantity && lineItem.amount) ? (lineItem.amount / quantity) : (lineItem.unitPrice || 0);

      let lineTax = 0;
      let lineNonRecoverable = null;
      if (linePlan) {
        lineTax = linePlan[lineIndex].tax;
        lineNonRecoverable = jtCaPlan ? linePlan[lineIndex].nonRecoverable : null;
      } else if (reviewedTax) {
        lineNonRecoverable = reviewedTax[lineIndex].nonRecoverable;
        lineTax = reviewedTax[lineIndex].tax;
      } else if (jtCaPlan) {
      const amount = lineItem.amount || 0;
      lineNonRecoverable = Math.round(amount * jtCaPlan.nonRecoverableRatePct) / 100;
      lineTax = Math.round(amount * jtCaPlan.recoverableRatePct) / 100 + lineNonRecoverable;
    } else if (hasTax && invoiceTaxTotal && eligibleSubtotal) {
      lineTax = Math.round(((lineItem.amount || 0) / eligibleSubtotal) * invoiceTaxTotal * 100) / 100;
    }

    return {
      invoiceLineItem: {
        description: lineItem.description || '',
        quantity,
        unitAmount,
        taxType: hasTax && (!linePlan || lineTax !== 0) ? 'INPUT2' : 'NONE',
        taxAmount: lineTax,
        nonRecoverableTax: lineNonRecoverable,
        gstTreatment: null
      },
      originalItem: {
        id: costItem?.id || null,
        name: costItem?.name || lineItem.description || '',
        costTypeId: costItem?.costTypeId || null,
        jobCostItemId: costItem?.jobCostItemId || costItem?.id || null
      },
      costGroupId: costItem?.costGroupId || null,
      isSplitAssignment: false,
      hasGST: hasTax
    };
  });

  if (!jtCaPlan && !linePlan && hasTax && invoiceTaxTotal && jtItems.length) {
    const lineSum = jtItems.reduce((sum, it) => sum + it.invoiceLineItem.taxAmount, 0);
    const remainder = Math.round((invoiceTaxTotal - lineSum) * 100) / 100;
    if (remainder !== 0) {
      const largest = jtItems.reduce((a, b) => (Math.abs(b.invoiceLineItem.unitAmount * b.invoiceLineItem.quantity) > Math.abs(a.invoiceLineItem.unitAmount * a.invoiceLineItem.quantity) ? b : a));
      largest.invoiceLineItem.taxAmount = Math.round((largest.invoiceLineItem.taxAmount + remainder) * 100) / 100;
    }
  }

  // Whole-bill split replaces the supplier lines with one JobTread line per
  // budget item. Tax is a share of the line tax above, remainder on the largest.
  const entryItems = splitMode
    ? splitAssignmentItems({
        jtItems,
        splitLines: splitLinesFor({ costGroups, splitTarget, splitCostTypes, total: eligibleSubtotal }),
        hasTax,
        jtCaPlan,
        invoiceNumber: extractedData.invoiceNumber
      })
    : jtItems;

  const lineModeRatePct = linePlan ? Math.max(0, ...linePlan.map(p => p.ratePct)) : 0;
  const taxRate = jtCaPlan
    ? jtCaPlan.recoverableRatePct / 100
    : linePlan && lineModeRatePct > 0
      ? lineModeRatePct / 100
      : (extractedData.taxRate ? extractedData.taxRate / 100 : (country === 'CA' ? 0 : 0.15));
  const taxName = jtCaPlan ? jtCaPlan.recoverableTaxName : 'GST';

  return {
    vendorId: selectedVendorId,
    xeroInvoiceID: xeroBillId,
    isCreditNote: isCreditNote || false,
    markAsPaid: markAsPaid === true,
    directBillUploadId: upload?.uploadId,
    s3Key: upload?.s3Key,
    pdfFileName: upload?.fileName,
    mimeType: upload?.mimeType,
    billInfo: {
      invoiceNumber: extractedData.invoiceNumber || '',
      invoiceID: xeroBillId,
      contactName: extractedData.vendorName,
      date: extractedData.issueDate || new Date().toISOString().split('T')[0],
      dueDate: extractedData.dueDate || null,
      lineAmountTypes,
      total: extractedData.total,
      currency: xeroCurrency || extractedData.currency || null,
      exchangeRate: xeroCurrencyRate
    },
    taxConfig: { rate: taxRate, name: hasTax ? taxName : 'No Tax', nonRecoverableName: jtCaPlan?.nonRecoverableTaxName || null },
    billEntries: [{
      mode: isJobMode ? 'job' : 'po',
      jobId,
      vendorOrderId: matchedPO?.vendorOrderId || null,
      vendorOrderNumber: matchedPO?.vendorOrderNumber || null,
      items: entryItems
    }]
  };
}

function splitLinesFor({ costGroups, splitTarget, splitCostTypes, total }) {
  if (!splitTarget?.id) return [];
  return computeProRataSplit(itemsForTarget(costGroups || [], splitTarget, splitCostTypes || []), total).lines;
}

function splitAssignmentItems({ jtItems, splitLines, hasTax, jtCaPlan, invoiceNumber }) {
  const lineTaxTotal = Math.round(jtItems.reduce((s, it) => s + (it.invoiceLineItem.taxAmount || 0), 0) * 100) / 100;
  const linePstTotal = Math.round(jtItems.reduce((s, it) => s + (Number(it.invoiceLineItem.nonRecoverableTax) || 0), 0) * 100) / 100;
  const splitTotal = splitLines.reduce((s, l) => s + l.amount, 0);
  const share = (amount, total) => (splitTotal ? Math.round((amount / splitTotal) * total * 100) / 100 : 0);
  const splitItems = splitLines.map(({ item, amount, ratio }) => ({
    invoiceLineItem: {
      description: `${item.name} (${(ratio * 100).toFixed(1)}% of bill${invoiceNumber ? ' ' + invoiceNumber : ''})`,
      quantity: 1,
      unitAmount: amount,
      taxType: hasTax ? 'INPUT2' : 'NONE',
      taxAmount: hasTax ? share(amount, lineTaxTotal) : 0,
      nonRecoverableTax: jtCaPlan && hasTax ? share(amount, linePstTotal) : null,
      gstTreatment: null
    },
    originalItem: {
      id: item.id,
      name: item.name,
      costTypeId: item.costTypeId || null,
      jobCostItemId: item.jobCostItemId || item.id
    },
    costGroupId: item.costGroupId || null,
    isSplitAssignment: true,
    hasGST: hasTax
  }));
  if (hasTax && splitItems.length) {
    const largest = splitItems.reduce((a, b) => (Math.abs(b.invoiceLineItem.unitAmount) > Math.abs(a.invoiceLineItem.unitAmount) ? b : a));
    const taxGap = Math.round((lineTaxTotal - splitItems.reduce((s, it) => s + it.invoiceLineItem.taxAmount, 0)) * 100) / 100;
    largest.invoiceLineItem.taxAmount = Math.round((largest.invoiceLineItem.taxAmount + taxGap) * 100) / 100;
    if (jtCaPlan) {
      const pstGap = Math.round((linePstTotal - splitItems.reduce((s, it) => s + (it.invoiceLineItem.nonRecoverableTax || 0), 0)) * 100) / 100;
      largest.invoiceLineItem.nonRecoverableTax = Math.round((largest.invoiceLineItem.nonRecoverableTax + pstGap) * 100) / 100;
    }
  }
  return splitItems;
}
